import { Chip, styled } from '@mui/material';
import { useNotInViewCount } from 'hooks/useNotInViewCount';
import { useState, type RefObject } from 'react';
import { ChipType } from '../Filters';
import { AllChipsPopover } from './AllChipsPopover';

type FilterChipsProps = {
  chips: ChipType[];
  onClearAllFilters: () => void;
  filterBoxElementRef?: RefObject<HTMLDivElement>;
  variant?: 'primary' | 'secondary';
};

export const FilterChip = styled(Chip)(({ theme }) => ({
  borderRadius: '4px'
}));

const ChipWrapper = ({ chip, variant = 'primary' }: { chip: ChipType; variant?: 'primary' | 'secondary' }) => {
  return (
    <div
      style={{
        height: '100%',
        display: 'grid',
        alignItems: 'center'
      }}
    >
      <FilterChip
        key={chip.key}
        label={chip.value}
        onDelete={chip.onDelete}
        size="small"
        clickable
        data-testid="filter-chip"
        color={variant === 'primary' ? 'primary' : undefined}
      />
    </div>
  );
};

const MoreChips = ({ numberOfHidden, onClick }: { numberOfHidden: number; onClick: () => void }) => {
  return (
    <>
      <div>
        {Boolean(numberOfHidden) && <Chip label={`+${numberOfHidden}`} size="small" clickable onClick={onClick} />}
      </div>
    </>
  );
};

const ChipsWrapper = styled('div')({
  display: 'flex',
  flexWrap: 'wrap',
  alignContent: 'start',
  gap: '4px',
  maxHeight: '40px',
  height: '100%'
});

export const FilterChips = ({ chips, onClearAllFilters, filterBoxElementRef, variant }: FilterChipsProps) => {
  const [anchorElement, setAnchorElement] = useState<Element>();
  const { containerRef, notInViewCount } = useNotInViewCount([chips.length.toString()]);

  const openAllChipsPopover = () => {
    if (chips.length === 0) return;
    if (filterBoxElementRef?.current) setAnchorElement(filterBoxElementRef.current);
  };

  return (
    <>
      <ChipsWrapper onClick={openAllChipsPopover} ref={containerRef}>
        {chips.map(chip => (
          <ChipWrapper key={chip.key} chip={chip} variant={variant} />
        ))}
      </ChipsWrapper>
      <MoreChips numberOfHidden={notInViewCount} onClick={openAllChipsPopover} />
      <AllChipsPopover
        variant={variant}
        chips={chips}
        onClearAll={onClearAllFilters}
        open={anchorElement !== undefined}
        onClose={() => setAnchorElement(undefined)}
        anchorEl={anchorElement}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      />
    </>
  );
};
